// One-line text that truncates INTELLIGENTLY instead of CSS ellipsis.
//
// CSS `text-overflow: ellipsis` always cuts the tail — which is exactly the
// distinguishing part of strings like "Knowledge graph — Netiva, LLC —
// Operating Agreement (cockpit rerun)". FitText measures the actual rendered
// width with Pretext (canvas-accurate, no DOM reflow) and middle-truncates:
// head kept, tail kept, "…" where the redundancy was. Full text stays in the
// tooltip. `keep="tail"` flips to head-elision for path-like strings.
import { useLayoutEffect, useRef, useState } from 'react';
import { prepareWithSegments, measureNaturalWidth } from '@chenglou/pretext';

// Pretext caches prepare() internally per (text, font); this memo avoids even
// the cache lookup for the hot strings lists re-render constantly.
const widthCache = new Map();
function textWidth(text, font) {
  const key = `${font}\u0000${text}`;
  let w = widthCache.get(key);
  if (w === undefined) {
    try {
      w = measureNaturalWidth(prepareWithSegments(text, font));
    } catch {
      w = text.length * 8; // Intl.Segmenter missing — degrade to a guess
    }
    if (widthCache.size > 2000) widthCache.clear();
    widthCache.set(key, w);
  }
  return w;
}

// Largest rendering of `text` that fits `maxWidth`: binary search over how
// many characters survive, split between head and tail.
function fitString(text, font, maxWidth, keep) {
  if (textWidth(text, font) <= maxWidth) return text;
  const render = (n) => {
    if (keep === 'tail') return `…${text.slice(text.length - n)}`;
    if (keep === 'head') return `${text.slice(0, n)}…`;
    const head = Math.ceil(n * 0.55);
    return `${text.slice(0, head)} … ${text.slice(text.length - (n - head))}`;
  };
  let lo = 1;
  let hi = text.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (textWidth(render(mid), font) <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return render(lo);
}

export default function FitText({ text, keep = 'middle', className, style, title }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(text);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const refit = () => {
      const width = el.clientWidth;
      if (!width) return;
      const cs = getComputedStyle(el);
      const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      // 2% margin: the app font stack starts at -apple-system, where canvas
      // and DOM measurement can disagree by a hair (see pretext caveats).
      setShown(fitString(String(text ?? ''), font, width * 0.98, keep));
    };
    refit();
    const ro = new ResizeObserver(refit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, keep]);

  const truncated = shown !== text;
  return (
    <span
      ref={ref}
      className={className}
      style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', minWidth: 0, ...style }}
      title={title || (truncated ? String(text) : undefined)}
    >
      {shown}
    </span>
  );
}
